import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Cpu, Edit, Eye, EyeOff, FileText, Globe, Plus, Save, Trash2 } from 'lucide-react';
import { generateIcSvg, generateIcPinLayout } from '../utils/icSvgGenerator';
import { IC_PIN_PRESETS, getIcPinPresetOptions } from '../utils/icPinPresets';
import PinMetadataEditor from './PinMetadataEditor';
import { adminIcPinoutsAPI } from '../services/adminApi';
import { appUrl } from '../utils/appPaths';
import {
  getEditablePinAlternateNameEntries,
  serializePinAlternateNames,
} from '../utils/pinAlternateNames';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MutedText = styled.p`
  margin: 0;
  color: #6b7280;
  line-height: 1.5;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatusPill = styled.div`
  padding: 8px 16px;
  background: #2c9fa4;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const StepCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
`;

const StepTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditingSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const EditingEyebrow = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
`;

const EditingTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
`;

const EditingMeta = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PreviewPanel = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  min-width: 0;
`;

const PanelTitle = styled.h4`
  margin: 0 0 12px;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 15px;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 15px;
  background: white;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const getButtonBackground = ({ $variant = 'primary', $active = false }) => {
  if ($active) {
    return '#2c9fa4';
  }

  switch ($variant) {
    case 'neutral':
      return '#f3f4f6';
    case 'secondary':
      return '#6b7280';
    case 'success':
      return '#10b981';
    case 'warning':
      return '#f59e0b';
    case 'danger':
      return '#dc2626';
    default:
      return '#2c9fa4';
  }
};

const Button = styled.button`
  padding: ${props => (props.$compact ? '6px 12px' : '8px 16px')};
  border: none;
  border-radius: ${props => (props.$compact ? '4px' : '6px')};
  background: ${getButtonBackground};
  color: ${props => (props.$variant === 'neutral' && !props.$active ? '#374151' : 'white')};
  font-size: ${props => (props.$compact ? '12px' : '14px')};
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover {
    opacity: ${props => (props.disabled ? 1 : 0.92)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const StatusBanner = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid
    ${props => (props.$tone === 'error' ? '#fecaca' : '#bbf7d0')};
  background: ${props => (props.$tone === 'error' ? '#fef2f2' : '#f0fdf4')};
  color: ${props => (props.$tone === 'error' ? '#b91c1c' : '#166534')};
  font-size: 14px;
  font-weight: 500;
`;

const PreviewBox = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  min-height: 360px;
  overflow: auto;

  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
    margin: 0 auto;
    max-width: 100%;
    height: auto;
  }
`;

const PayloadPreview = styled.pre`
  max-height: 360px;
  overflow: auto;
  background: #0f172a;
  color: #e2e8f0;
  padding: 16px;
  border-radius: 8px;
  font-size: 12px;
  margin: 0;
`;

const ManagePanel = styled.div`
  display: grid;
  gap: 16px;
`;

const IcCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 16px;
`;

const IdBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  background: #6b7280;
  color: white;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  background: ${props => (props.$published ? '#10b981' : '#f59e0b')};
  color: white;
`;

const VariantBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  background: #f1f5f9;
  color: #475569;
`;

const CardDescription = styled.p`
  margin: 0 0 8px 0;
  color: #6b7280;
  font-size: 14px;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const CenteredState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
  font-size: 16px;
`;

const normalizePinCount = (value) => {
  const count = Number.parseInt(value, 10);

  if (!Number.isFinite(count) || count < 2) {
    return 8;
  }

  if (count % 2 !== 0) {
    return count + 1;
  }

  return Math.min(count, 64);
};

const createPins = (pinCount) => (
  Array.from({ length: pinCount }, (_, index) => ({
    pin_number: index + 1,
    pin_name: `PIN${index + 1}`,
    pin_role: '',
    functions: '',
    alternate_pin_names: '',
    alternate_functions: '',
    description: '',
    current_limit_ma: '',
    voltage_min_v: '',
    voltage_max_v: '',
    is_5v_tolerant: false,
    capability_tags: []
  }))
);

const normalizeMeaningfulText = (value, fallback = '') => {
  const normalizedValue = String(value ?? '').trim();

  if (!normalizedValue) {
    return fallback;
  }

  const lowerValue = normalizedValue.toLowerCase();
  if (lowerValue === 'undefined' || lowerValue === 'null') {
    return fallback;
  }

  return normalizedValue;
};

const humanizeSlug = (value, fallback = 'Untitled IC') => {
  const normalizedSlug = normalizeMeaningfulText(value);
  if (!normalizedSlug) {
    return fallback;
  }

  return normalizedSlug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const normalizeIcRecord = (record) => {
  const numericId = Number.parseInt(record?.id, 10);
  const normalizedSlug = normalizeMeaningfulText(record?.slug);
  const fallbackName = humanizeSlug(
    normalizedSlug,
    Number.isInteger(numericId) && numericId > 0 ? `IC ${numericId}` : 'Untitled IC'
  );
  const normalizedPackageVariant = normalizeMeaningfulText(record?.package_variant);
  const normalizedPackageType = normalizeMeaningfulText(record?.package_type, 'DIP');
  const normalizedPinCount = Number.parseInt(record?.pin_count, 10);

  return {
    ...record,
    id: Number.isInteger(numericId) && numericId > 0 ? numericId : null,
    name: normalizeMeaningfulText(record?.name, fallbackName),
    slug: normalizedSlug,
    manufacturer: normalizeMeaningfulText(record?.manufacturer),
    category: normalizeMeaningfulText(record?.category),
    description: normalizeMeaningfulText(record?.description),
    package_variant: normalizedPackageVariant,
    package_type: normalizedPackageType,
    pin_count: Number.isInteger(normalizedPinCount) && normalizedPinCount > 0 ? normalizedPinCount : null,
    published: Number(record?.published) === 1 ? 1 : 0,
  };
};

const AdminIcPinouts = () => {
  const [icForm, setIcForm] = useState({
    name: '74HC595',
    slug: '74hc595',
    package_type: 'DIP',
    pin_count: 16,
    marker_style: 'notch-dot',
    label_mode: 'number-name',
    description: '',
    manufacturer: '',
    category: 'logic-ic',
    supply_voltage_min_v: '',
    supply_voltage_max_v: '',
    logic_voltage_min_v: '',
    logic_voltage_max_v: '',
    datasheet_url: '',
    published: false
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewMode, setViewMode] = useState('create');
  const [icPinouts, setIcPinouts] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState('');
  const [selectedPinKey, setSelectedPinKey] = useState('1');
  const [selectedPreset, setSelectedPreset] = useState('');
  const [pins, setPins] = useState(() => createPins(16));
  const [editingIcId, setEditingIcId] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const pinCount = normalizePinCount(icForm.pin_count);
  const editingIcNumericId = Number.parseInt(editingIcId, 10);
  const isEditingIc = Number.isInteger(editingIcNumericId) && editingIcNumericId > 0;
  const presetOptions = useMemo(() => getIcPinPresetOptions(), []);

  const generatedSvg = useMemo(() => (
    generateIcSvg({
      name: icForm.name,
      packageType: icForm.package_type,
      pinCount,
      markerStyle: icForm.marker_style,
      labelMode: icForm.label_mode,
      pins
    })
  ), [icForm.label_mode, icForm.marker_style, icForm.name, icForm.package_type, pinCount, pins]);

  const handlePinCountChange = (value) => {
    const nextPinCount = normalizePinCount(value);

    setIcForm(current => ({
      ...current,
      pin_count: nextPinCount
    }));

    setPins(currentPins => {
      const nextPins = createPins(nextPinCount);

      return nextPins.map((pin, index) => ({
        ...pin,
        ...(currentPins[index] || {})
      }));
    });

    setSelectedPinKey('1');
  };

  const buildIcPayload = () => ({
    ...icForm,
    pin_count: pinCount,
    pins,
    pin_layout: generatedPinLayout,
    svg_content: generatedSvg,
    package_variant: `${icForm.package_type}-${pinCount}`
  });

  const validateIcPayload = (payload) => {
    if (!payload.name.trim()) {
      return 'IC name is required.';
    }

    if (!payload.slug.trim()) {
      return 'Slug is required.';
    }

    if (!payload.package_type) {
      return 'Package type is required.';
    }

    if (!payload.pin_count || payload.pin_count < 2) {
      return 'Pin count is invalid.';
    }

    const invalidPin = payload.pins.find((pin) => !String(pin.pin_name || '').trim());

    if (invalidPin) {
      return `Pin ${invalidPin.pin_number} needs a pin name.`;
    }

    return '';
  };

  const togglePublishStatus = async (item) => {
    const normalizedItem = normalizeIcRecord(item);

    if (!normalizedItem.id) {
      setManageError('This IC pinout is missing a valid ID, so it cannot be published yet.');
      return;
    }

    const nextPublished = Number(normalizedItem.published) !== 1;

    try {
      setManageError('');
      setError('');
      await adminIcPinoutsAPI.publish(normalizedItem.id, nextPublished);

      setIcPinouts((currentItems) =>
        currentItems.map((currentItem) =>
          Number(currentItem.id) === Number(normalizedItem.id)
            ? { ...currentItem, published: nextPublished ? 1 : 0 }
            : currentItem
        )
      );

      setSuccess(nextPublished ? 'IC pinout published.' : 'IC pinout unpublished.');
    } catch (error) {
      console.error('Failed to update publish status:', error);
      setManageError(error?.response?.data?.error || 'Failed to update publish status.');
    }
  };

  const startNewIc = (nextViewMode = 'create') => {
    const resolvedViewMode = typeof nextViewMode === 'string' ? nextViewMode : 'create';

    setEditingIcId(null);
    setSelectedPreset('');
    setError('');
    setSuccess('');
    setIcForm({
      name: '74HC595',
      slug: '74hc595',
      package_type: 'DIP',
      pin_count: 16,
      marker_style: 'notch-dot',
      label_mode: 'name',
      description: '',
      manufacturer: '',
      category: 'logic-ic',
      supply_voltage_min_v: '',
      supply_voltage_max_v: '',
      logic_voltage_min_v: '',
      logic_voltage_max_v: '',
      datasheet_url: '',
      published: false
    });
    setPins(createPins(16));
    setSelectedPinKey('1');
    setViewMode(resolvedViewMode);
  };

  const closeEditIc = () => {
    startNewIc('manage');
  };

  const handleSavePrototype = async () => {
    const payload = buildIcPayload();
    const validationError = validateIcPayload(payload);

    if (validationError) {
      setError(validationError);
      setSuccess('');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = isEditingIc
        ? await adminIcPinoutsAPI.update(editingIcNumericId, payload)
        : await adminIcPinoutsAPI.create({
            ...payload,
            published: false
          });

      console.log('Saved IC pinout:', response.data);

      setSuccess(isEditingIc
        ? 'IC pinout updated successfully.'
        : 'IC pinout saved as draft.'
      );

      setEditingIcId(null);
      await fetchIcPinouts();
      setViewMode('manage');
    } catch (error) {
      console.error('Failed to save IC pinout:', error);
      setError(error?.response?.data?.error || 'Failed to save IC pinout.');
    } finally {
      setSaving(false);
    }
  };

  const deleteIcPinout = async (item) => {
    if (!item?.id) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${item.name || 'this IC pinout'}? This will also delete its package variant and pins.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setManageError('');
      setError('');
      await adminIcPinoutsAPI.delete(item.id);

      setIcPinouts((currentItems) =>
        currentItems.filter((currentItem) => Number(currentItem.id) !== Number(item.id))
      );

      if (editingIcNumericId === Number(item.id)) {
        startNewIc();
      }

      setSuccess('IC pinout deleted.');
    } catch (error) {
      console.error('Failed to delete IC pinout:', error);
      setManageError(error?.response?.data?.error || 'Failed to delete IC pinout.');
    }
  };

  const getPinKey = (pin) => String(pin.pin_number || pin.pinNumber || '');

  const selectedPin = useMemo(
    () => pins.find((pin) => getPinKey(pin) === selectedPinKey) || null,
    [pins, selectedPinKey]
  );

  const handlePinFieldChange = (pinKey, field, value) => {
    setPins((currentPins) =>
      currentPins.map((pin) =>
        getPinKey(pin) === String(pinKey)
          ? { ...pin, [field]: value }
          : pin
      )
    );
  };

  const handlePinAlternateNameChange = (pinKey, index, field, value) => {
    setPins((currentPins) =>
      currentPins.map((pin) => {
        if (getPinKey(pin) !== String(pinKey)) {
          return pin;
        }

        const currentNames = getEditablePinAlternateNameEntries(pin);
        currentNames[index] = {
          ...currentNames[index],
          [field]: value,
        };

        return {
          ...pin,
          alternate_pin_names: serializePinAlternateNames(currentNames)
        };
      })
    );
  };

  const generatedPinLayout = useMemo(() => (
    generateIcPinLayout({
      packageType: icForm.package_type,
      pinCount,
      pins
    })
  ), [icForm.package_type, pinCount, pins]);

  const applyPreset = (presetKey) => {
    setSelectedPreset(presetKey);

    if (!presetKey) {
      return;
    }

    const preset = IC_PIN_PRESETS[presetKey];

    if (!preset) {
      return;
    }

    setIcForm((current) => ({
      ...current,
      name: preset.name,
      slug: preset.slug,
      package_type: preset.package_type,
      pin_count: preset.pin_count,
      marker_style: preset.marker_style,
      label_mode: preset.label_mode || current.label_mode || 'name'
    }));

    setPins(preset.pins.map((pin) => ({ ...pin })));
    setSelectedPinKey('1');
  };

  const fetchIcPinouts = async () => {
    setManageLoading(true);
    setManageError('');

    try {
      const response = await adminIcPinoutsAPI.getAll();
      const rows = response.data?.data || response.data || [];
      setIcPinouts(Array.isArray(rows) ? rows.map(normalizeIcRecord) : []);
    } catch (error) {
      console.error('Failed to fetch IC pinouts:', error);
      setManageError(error?.response?.data?.error || 'Failed to fetch IC pinouts.');
    } finally {
      setManageLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'manage') {
      fetchIcPinouts();
    }
  }, [viewMode]);

  const openEditIc = async (item) => {
    const normalizedItem = normalizeIcRecord(item);

    if (!normalizedItem.id) {
      setManageError('This IC pinout is missing a valid ID, so it cannot be opened for editing.');
      return;
    }

    setLoadingEdit(true);
    setError('');
    setSuccess('');
    setManageError('');

    try {
      const response = await adminIcPinoutsAPI.getById(normalizedItem.id);
      const data = normalizeIcRecord(response.data?.data || response.data);

      setEditingIcId(data.id);

      setIcForm((current) => ({
        ...current,
        name: data.name || '',
        slug: data.slug || '',
        manufacturer: data.manufacturer || '',
        category: data.category || 'logic-ic',
        description: data.description || '',
        datasheet_url: normalizeMeaningfulText(data.datasheet_url) || '',
        supply_voltage_min_v: data.supply_voltage_min_v || '',
        supply_voltage_max_v: data.supply_voltage_max_v || '',
        logic_voltage_min_v: data.logic_voltage_min_v || '',
        logic_voltage_max_v: data.logic_voltage_max_v || '',
        package_type: data.package_type || 'DIP',
        pin_count: data.pin_count || 8,
        marker_style: data.marker_style || 'notch-dot',
        label_mode: data.label_mode || 'name',
        published: Boolean(data.published)
      }));

      setPins(Array.isArray(data.pins) ? data.pins : createPins(data.pin_count || 8));
      setSelectedPinKey('1');
      setSelectedPreset('');
      setViewMode('create');
    } catch (error) {
      console.error('Failed to load IC pinout:', error);
      setManageError(error?.response?.data?.error || 'Failed to load IC pinout.');
    } finally {
      setLoadingEdit(false);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderText>
          <Title>
            <Cpu size={24} />
            IC Pinouts
          </Title>
          <MutedText>
            {viewMode === 'manage'
              ? 'Manage existing IC pinouts'
              : isEditingIc
                ? 'Edit an existing IC pinout with the full builder'
                : 'Create new IC pinouts and manage pin metadata'}
          </MutedText>
        </HeaderText>

        <HeaderActions>
          <Button
            type="button"
            $variant="neutral"
            $active={viewMode === 'create'}
            onClick={() => startNewIc()}
          >
            <Plus size={16} />
            Create
          </Button>

          <Button
            type="button"
            $variant="neutral"
            $active={viewMode === 'manage'}
            onClick={() => (isEditingIc ? closeEditIc() : setViewMode('manage'))}
          >
            <FileText size={16} />
            Manage
          </Button>

          {isEditingIc && viewMode === 'create' && (
            <StatusPill>
              <Edit size={16} />
              Editing
            </StatusPill>
          )}
        </HeaderActions>
      </Header>
      {error && <StatusBanner $tone="error">{error}</StatusBanner>}
      {success && <StatusBanner $tone="success">{success}</StatusBanner>}
      {viewMode !== 'manage' ? (
        <>
          {loadingEdit ? (
            <StepCard>
              <CenteredState>Loading IC pinout editor...</CenteredState>
            </StepCard>
          ) : (
            <>
              {isEditingIc && (
                <StepCard>
                  <EditingSummary>
                    <div>
                      <EditingEyebrow>Editing IC</EditingEyebrow>
                      <EditingTitle>
                        {icForm.name || 'Untitled IC'} ({icForm.package_type}-{pinCount})
                      </EditingTitle>
                      <EditingMeta>IC ID: {editingIcNumericId}</EditingMeta>
                    </div>
                    <Button
                      type="button"
                      $variant="neutral"
                      onClick={closeEditIc}
                    >
                      Back to Manage
                    </Button>
                  </EditingSummary>
                </StepCard>
              )}

              <StepCard>
                <StepTitle>
                  <Cpu size={20} />
                  Step 1: Configure IC
                </StepTitle>

                <FieldsGrid>
                  <FormGroup>
                    <Label>Preset</Label>
                    <Select
                      value={selectedPreset}
                      onChange={(e) => applyPreset(e.target.value)}
                    >
                      <option value="">Blank / Custom IC</option>
                      {presetOptions.map((preset) => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>IC Name</Label>
                    <Input
                      value={icForm.name}
                      onChange={(e) => {
                        setSelectedPreset('');
                        setIcForm({ ...icForm, name: e.target.value });
                      }}
                      placeholder="e.g. 74HC595"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Slug</Label>
                    <Input
                      value={icForm.slug}
                      onChange={(e) => setIcForm({ ...icForm, slug: e.target.value })}
                      placeholder="e.g. 74hc595"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Manufacturer</Label>
                    <Input
                      value={icForm.manufacturer}
                      onChange={(e) => setIcForm({ ...icForm, manufacturer: e.target.value })}
                      placeholder="e.g. Texas Instruments"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Package Type</Label>
                    <Select
                      value={icForm.package_type}
                      onChange={(e) => setIcForm({ ...icForm, package_type: e.target.value })}
                    >
                      <option value="DIP">DIP</option>
                      <option value="SOIC">SOIC</option>
                      <option value="TSSOP">TSSOP</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Category</Label>
                    <Select
                      value={icForm.category}
                      onChange={(e) => {
                        setSelectedPreset('');
                        setIcForm({ ...icForm, category: e.target.value });
                      }}
                    >
                      <option value="logic-ic">Logic IC</option>
                      <option value="timer-ic">Timer IC</option>
                      <option value="op-amp">Op-Amp / Comparator</option>
                      <option value="driver-ic">Driver IC</option>
                      <option value="power-ic">Power IC</option>
                      <option value="interface-ic">Interface IC</option>
                      <option value="microcontroller-ic">Microcontroller IC</option>
                      <option value="memory-ic">Memory IC</option>
                      <option value="other-ic">Other IC</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Pin Count</Label>
                    <Select
                      value={pinCount}
                      onChange={(e) => {
                        setSelectedPreset('');
                        handlePinCountChange(e.target.value);
                      }}
                    >
                      {[6, 8, 14, 16, 18, 20, 24, 28, 32, 40].map((count) => (
                        <option key={count} value={count}>
                          {count} pins
                        </option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Pin 1 Marker</Label>
                    <Select
                      value={icForm.marker_style}
                      onChange={(e) => setIcForm({ ...icForm, marker_style: e.target.value })}
                    >
                      <option value="notch-dot">Notch + Dot</option>
                      <option value="notch">Notch Only</option>
                      <option value="dot">Dot Only</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Label Mode</Label>
                    <Select
                      value={icForm.label_mode}
                      onChange={(e) => setIcForm({ ...icForm, label_mode: e.target.value })}
                    >
                      <option value="number-name">Pin number + name</option>
                      <option value="number">Pin number only</option>
                      <option value="name">Pin name only</option>
                      <option value="none">No side labels</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Datasheet URL</Label>
                    <Input
                      type="url"
                      value={icForm.datasheet_url}
                      onChange={(e) => setIcForm({ ...icForm, datasheet_url: e.target.value })}
                      placeholder="https://example.com/datasheet.pdf"
                    />
                  </FormGroup>

                  <FormGroup style={{ gridColumn: '1 / -1' }}>
                    <Label>Description</Label>
                    <TextArea
                      value={icForm.description}
                      onChange={(e) => setIcForm({ ...icForm, description: e.target.value })}
                      placeholder="Short description of this IC..."
                    />
                  </FormGroup>
                </FieldsGrid>

                <ActionRow>
                  <Button
                    type="button"
                    $variant="secondary"
                    onClick={() => handlePinCountChange(pinCount)}
                  >
                    <Plus size={16} />
                    Regenerate Pins
                  </Button>
                </ActionRow>
              </StepCard>

              <StepCard>
                <StepTitle>
                  <Eye size={20} />
                  Step 2: Review Preview
                </StepTitle>

                <PreviewGrid>
                  <PreviewPanel>
                    <PanelTitle>Generated SVG Preview</PanelTitle>
                    <PreviewBox dangerouslySetInnerHTML={{ __html: generatedSvg }} />
                  </PreviewPanel>

                  <PreviewPanel>
                    <PanelTitle>Generated Payload Preview</PanelTitle>
                    <PayloadPreview>
                      {JSON.stringify(buildIcPayload(), null, 2)}
                    </PayloadPreview>
                  </PreviewPanel>
                </PreviewGrid>

                <ActionRow style={{ marginTop: '20px' }}>
                  <Button type="button" onClick={handleSavePrototype} disabled={saving}>
                    <Save size={16} />
                    {saving ? 'Saving...' : isEditingIc ? 'Save Changes' : 'Save Draft'}
                  </Button>

                  {isEditingIc && (
                    <Button
                      type="button"
                      $variant="neutral"
                      onClick={closeEditIc}
                    >
                      Back to Manage
                    </Button>
                  )}
                </ActionRow>
              </StepCard>

              <StepCard>
                <StepTitle>
                  <Edit size={20} />
                  Step 3: Edit Pin Metadata
                </StepTitle>

                <PinMetadataEditor
                  pins={pins}
                  selectedPinKey={selectedPinKey}
                  onSelectedPinKeyChange={setSelectedPinKey}
                  onPinFieldChange={handlePinFieldChange}
                  onPinAlternateNameChange={handlePinAlternateNameChange}
                  getPinKey={getPinKey}
                  selectedPin={selectedPin}
                  disabled={false}
                />
              </StepCard>
            </>
          )}
        </>
      ) : (
      <ManagePanel>
        {manageLoading ? (
          <StepCard>
            <CenteredState>Loading IC pinouts...</CenteredState>
          </StepCard>
        ) : manageError ? (
          <StepCard>
            <CenteredState style={{ color: '#dc2626' }}>{manageError}</CenteredState>
            <ActionRow style={{ justifyContent: 'center' }}>
              <Button type="button" onClick={fetchIcPinouts}>
                Retry
              </Button>
            </ActionRow>
          </StepCard>
        ) : icPinouts.length === 0 ? (
          <EmptyState>
            <FileText size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px 0', color: '#6b7280' }}>No IC pinouts found</h3>
            <p style={{ margin: 0, color: '#9ca3af' }}>
              Create your first IC pinout using the "Create" tab.
            </p>
          </EmptyState>
        ) : (
          icPinouts.map((item) => {
            const isPublished = Number(item.published) === 1;
            const metadata = [
              item.manufacturer,
              item.category || 'Uncategorized',
              item.pin_count ? `${item.pin_count} pins` : null,
              item.slug ? `slug: ${item.slug}` : null
            ].filter(Boolean).join(' | ');

            return (
              <IcCard key={`${item.id}-${item.package_variant_id || 'no-variant'}`}>
                <div style={{ flex: 1 }}>
                  <CardTitleRow>
                    <CardTitle>{item.name}</CardTitle>
                    <IdBadge>ID: {item.id}</IdBadge>
                    <Badge $published={isPublished}>
                      {isPublished ? 'PUBLISHED' : 'DRAFT'}
                    </Badge>
                    {item.package_variant && (
                      <VariantBadge>{item.package_variant}</VariantBadge>
                    )}
                  </CardTitleRow>

                  {item.description && (
                    <CardDescription>{item.description}</CardDescription>
                  )}

                  <CardMeta>{metadata}</CardMeta>
                </div>

                <CardActions>
                  <Button
                    type="button"
                    $variant="secondary"
                    $compact
                    onClick={() => openEditIc(item)}
                    disabled={loadingEdit}
                  >
                    <Edit size={14} />
                    Edit
                  </Button>

                  <Button
                    type="button"
                    $variant={isPublished ? 'warning' : 'success'}
                    $compact
                    onClick={() => togglePublishStatus(item)}
                  >
                    {isPublished ? <EyeOff size={14} /> : <Globe size={14} />}
                    {isPublished ? 'Unpublish' : 'Publish'}
                  </Button>

                  {isPublished && (
                    <Button
                      type="button"
                      $compact
                      onClick={() => window.open(appUrl(`/ic-pinouts/${item.slug}`), '_blank')}
                    >
                      <Eye size={14} />
                      View
                    </Button>
                  )}

                  <Button
                    type="button"
                    $variant="danger"
                    $compact
                    onClick={() => deleteIcPinout(item)}
                  >
                    <Trash2 size={14} />
                    Delete
                  </Button>
                </CardActions>
              </IcCard>
            );
          })
        )}
      </ManagePanel>
      )}
    </Container>
  );
};

export default AdminIcPinouts;


